import React from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArcSocketContext, ChannelResponseEnum } from '@src/core/socket';
import { CameraEntity, CommandStatusEnum, FlightEntity, FlightOrderDataEntity, FLightOrderStatusEnum, TelemetryAttiudeEulerEntity, TelemetryBatteryEntity, TelemetryDistanceSensorEntity, TelemetryEventEntity, TelemetryStatusTextEntity, TelemetryTypeEnum, TelemetryVelocityNedEntity, TelemetryVhrHudEntity } from '@src/features/order/entities';
import { IFlightContext } from "./types";
import { routesConfig } from '@src/core/routes';
import { TelemetryGpsInfoEntity } from '@src/features/order/entities/telemetry_gps_info';
import { rememberFlightPosition } from './flight-position-cache';
import { normalizeGpsInfo } from './normalize-gps-info';

const EMPTY_FLIGHT: FlightEntity = {};
const EMPTY_CAMERA: CameraEntity = {};
const EMPTY_BATTERY = {} as TelemetryBatteryEntity;
const EMPTY_VELOCITY = {} as TelemetryVelocityNedEntity;
const EMPTY_ATTITUDE = {} as TelemetryAttiudeEulerEntity;
const EMPTY_DISTANCE = {} as TelemetryDistanceSensorEntity;
const EMPTY_GPS = {} as TelemetryGpsInfoEntity;
const EMPTY_VHR = {} as TelemetryVhrHudEntity;

export const FlightContext = React.createContext<IFlightContext>({
  flightData: EMPTY_FLIGHT,
  cameraData: EMPTY_CAMERA,
});

export const FlightProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

  const { subscribe, unsubscribe } = React.useContext(ArcSocketContext);

  const navigate = useNavigate();
  const [flightData, setFlightData] = React.useState<FlightEntity>(EMPTY_FLIGHT);
  const [flightDataByMissionId, setFlightDataByMissionId] = React.useState<Record<string, FlightEntity>>({});
  const [cameraData, setCameraData] = React.useState<CameraEntity>(EMPTY_CAMERA);

  const [flightMode, setFlightMode] = React.useState<{missionId: string, value: string}>();
  const [battery, setBattery] = React.useState<TelemetryBatteryEntity>();
  const [status, setStatus] = React.useState<TelemetryStatusTextEntity>();
  const [velocityNed, setVelocityNed] = React.useState<TelemetryVelocityNedEntity>();
  const [attitudeEuler, setAttitudeEuler] = React.useState<TelemetryAttiudeEulerEntity>();
  const [distanceSensor, setDistanceSensor] = React.useState<TelemetryDistanceSensorEntity>();
  const [gpsInfo, setGpsInfo] = React.useState<TelemetryGpsInfoEntity>();
  const [vhrNud, setVhrNud] = React.useState<TelemetryVhrHudEntity>();

  React.useEffect(() => {
    subscribe(ChannelResponseEnum.TELEMETRY, (event: TelemetryEventEntity) => {
      if (event.telemetry_type === TelemetryTypeEnum.FlightMode) {
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.StatusText) {
        const value = event.value as { type: string; text: string };
        setStatus({
          text: value?.text || '',
          missionId: event.currentMissionId ?? '',
          type: value?.type || event.telemetry_type,
        } as TelemetryStatusTextEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.Battery) {
        setBattery((event.value ?? EMPTY_BATTERY) as TelemetryBatteryEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.VelocityNed) {
        setVelocityNed((event.value ?? EMPTY_VELOCITY) as TelemetryVelocityNedEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.AttitudeEuler) {
        setAttitudeEuler((event.value ?? EMPTY_ATTITUDE) as TelemetryAttiudeEulerEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.DistanceSensor) {
        setDistanceSensor((event.value ?? EMPTY_DISTANCE) as TelemetryDistanceSensorEntity);
        return;
      }
      if (event.telemetry_type === TelemetryTypeEnum.GpsInfo) {
        setGpsInfo(normalizeGpsInfo(event));
        return;
      }
      if(event.telemetry_type === TelemetryTypeEnum.VhrNud) {
        setVhrNud((event.value ?? EMPTY_VHR) as TelemetryVhrHudEntity);
        return;
      }
    });
    subscribe(ChannelResponseEnum.FLIGHT_DATA, (event: { flightData: FlightEntity }) => {
      const fd = event.flightData ?? EMPTY_FLIGHT;
      setFlightData(fd);
      if (fd.currentMissionId) {
        setFlightDataByMissionId((prev) => ({
          ...prev,
          [fd.currentMissionId as string]: fd,
        }));
      }
      if (fd.currentMissionId && fd.flightMode) {
        setFlightMode({ missionId: fd.currentMissionId, value: fd.flightMode });
      }
      rememberFlightPosition(
        fd.serialNumber as string | undefined,
        fd.latitude,
        fd.longitude,
        fd.heading
      );
    });
    subscribe(ChannelResponseEnum.CAMERA_DATA, (data: CameraEntity) => {
      setCameraData(data ?? EMPTY_CAMERA);
    });
    subscribe(ChannelResponseEnum.COMMAND_STATUS, (data) => {
      if (data.command === CommandStatusEnum.DELIVERY_COMPLETED) {
        if (data.status === 'ERROR') {
          toast.error('[DELIVERY_COMPLETED]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');

      }
      if (data.command === CommandStatusEnum.START_MANUAL_CONTROL) {
        if (data.status === 'ERROR') {
          toast.error('[START_MANUAL_CONTROL]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_DELIVERY) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_DELIVERY]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_LOCK) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_LOCK]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_UNLOCK) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_UNLOCK]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_REEL_DOWN) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_REEL_DOWN]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
      if (data.command === CommandStatusEnum.WINCH_REEL_UP) {
        if (data.status === 'ERROR') {
          toast.error('[WINCH_REEL_UP]: Something went wrong!');
          return;
        }
        toast.success(data.status ?? '');
      }
    });

    subscribe(ChannelResponseEnum.ORDER_STATUS, (event: { data: FlightOrderDataEntity }) => {
      if (event.data.status === FLightOrderStatusEnum.COMPLETED) {
        toast.success('Order Status Completed Text');
        const path =
          typeof window !== 'undefined' ? window.location.pathname : '';
        const onViewerMission =
          path.includes('/en-route-viewer') ||
          path.includes('/ready-for-take-off-viewer');
        const onViewerHub =
          path === routesConfig.viewer ||
          path.startsWith(`${routesConfig.viewer}/`);
        if (onViewerMission) {
          navigate(routesConfig.viewer, { replace: true });
        } else if (!onViewerHub) {
          navigate(routesConfig.orders, { replace: true });
        }
        return;
      }
    });

    return () => {
      unsubscribe(ChannelResponseEnum.TELEMETRY);
      unsubscribe(ChannelResponseEnum.FLIGHT_DATA);
      unsubscribe(ChannelResponseEnum.CAMERA_DATA);
      unsubscribe(ChannelResponseEnum.ORDER_STATUS);
      unsubscribe(ChannelResponseEnum.COMMAND_STATUS);
    };
  }, [subscribe, unsubscribe, navigate]);


  const value = React.useMemo(() => ({
    status,
    battery,
    flightData,
    flightDataByMissionId,
    velocityNed,
    attitudeEuler,
    distanceSensor,
    flightMode,
    cameraData,
    gpsInfo,
    vhrNud,
  }), [status, battery, flightData, flightDataByMissionId, velocityNed, attitudeEuler, distanceSensor, flightMode, cameraData, gpsInfo, vhrNud]);

  return (
    <FlightContext.Provider value={value}>
      {children}
    </FlightContext.Provider>
  );
};